/**
 * Created by DSadler on 20/04/2017.
 *
 * This file sets the base definitions for content items. These include all of the UI information to show these in the
 * interface, as well as the base style definitions to create the elements. These also provide part of the default
 * information for elements to fall back on if reset. Primitive components are not opinionated - they will do exactly
 * what they are told. Therefore base styles and resets must be set correctly here.
 *
 * Resetting a components' styles will make it revert to its item definition. These can be overridden using JSON in the
 * the editor, however this is an additive merge (not replacement), so that defaults can still exist.
 *
 * Composite elements can also be defined here, by nesting child elements. Note that these child elements must each
 * correspond to an item definition, because these composites are just a way of creating multiple items at once.
 *
 * The initial state here is defined using javascript to make cross-linking easier; obviously not possible in JSON.
 *
 * Definition hierarchy:
 *
 * Primitive component -> Item definition -> Item state
 *
 * Components calculate their state in this order. State always wins, and merges are not destructive - passing null
 * does not wipe a property, you need to pass an empty item to overwrite.
 *
 */


import {BASE_DEFINITION, CONTENT_DEFINITION_CATEGORIES} from "../../../constants";
/**
 * Created by DSadler on 19/04/2017.
 */

/* Define content component types */
const CONTENT_TYPES = {
  CELL_ITEM: "CELL_ITEM",
  ROW_ITEM: "ROW_ITEM",
  TABLE_ITEM: "TABLE_ITEM",
  RICH_TEXT: "RICH_TEXT",
  IMAGE_ITEM: "IMAGE_ITEM",
  TOC_ITEM: "TOC_ITEM",
  MAIN_ROW: "MAIN_ROW",
  COLUMN: "COLUMN",
};

/* eslint-disable no-unused-vars */
const COLOURS = {
  abiMidnightBlue : "#230052",
  abiPurple: "#7f58a5",
  abiBlue: "#6e9bb0",
  abiRed: "#ce0c4e",
  abiOrange: "#f26334",
  abiGreen: "#a8ad2c",
  black: "#231f20",
  grey: "#939596",
  aqua: "#53c0a5",
  stone: "#c59872",
  mauve: "#7f2c72",
  yellow: "#fcb316",
};

const FONTS = {
  core: "arial, sans-serif",
  accent: "times new roman, serif",
};

const FONT_SIZES = {
  headings: "26px",
  body: "12px",
  small: "10px",
};

const LINE_HEIGHTS = {
  headings: "34px",
  body: "18px",
  small: "normal"
};
/* eslint-enable no-unused-vars */

/**
 * The default built-in content definitions
 * @type {Array.<ContentDefinition>}
 */
const CONTENT_DEFINITIONS = (() => {
  
  /**** Primitive Definitions ****/
  
  var TABLE_ITEM = {
    ...BASE_DEFINITION,
    configId: "TABLE_ITEM",
    configName: "Table",
    configDescription: "A basic unstyled table, for containing rows. Will collapse to the size of the smallest element.",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STRUCTURAL_COMPONENTS,
    configPrimitive: CONTENT_TYPES.TABLE_ITEM,
    editorAllowedParents: [
      {
        configPrimitive: CONTENT_TYPES.CELL_ITEM,
      }
    ],
    viewAttributes: {
      "border": 0,
      "cellPadding": 0,
      "cellSpacing": 0,
    },
    viewClass: "table-item",
  };
  
  var ROW_ITEM = {
    ...BASE_DEFINITION,
    configId: "ROW_ITEM",
    configName: "Row",
    configDescription: "A basic unstyled row container, should be placed in a table item.",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STRUCTURAL_COMPONENTS,
    configPrimitive: CONTENT_TYPES.ROW_ITEM,
    editorAllowedParents: [
      {
        configPrimitive: CONTENT_TYPES.TABLE_ITEM,
      },
      {
        configId: "COLUMN",
      }
    ],
    viewAttributes: {
      align: "center",
      valign: "top",
    },
    viewClass: "row-item",
    //styleTextAlignment: "center",
  };
  
  var CELL_ITEM = {
    ...BASE_DEFINITION,
    configId: "CELL_ITEM",
    configName: "Cell",
    configDescription: "A basic unstyled content cell, should be placed in a table row.",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STRUCTURAL_COMPONENTS,
    configPrimitive: CONTENT_TYPES.CELL_ITEM,
    viewAttributes: {
      valign: "top",
      align: "center",
    },
    viewClass: "cell-item",
    editorAllowedParents: [
      {
        configPrimitive: CONTENT_TYPES.ROW_ITEM,
      },
      {
        configPrimitive: CONTENT_TYPES.MAIN_ROW,
      }
    ],
  };
  
  var IMAGE_ITEM = {
    ...BASE_DEFINITION,
    configId: "IMAGE_ITEM",
    configName: "Image",
    configDescription: "A single image, selectable from the image library or setting manually",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STANDARD_COMPONENTS,
    configPrimitive: CONTENT_TYPES.IMAGE_ITEM,
    editorAllowedParents: [
      {
        configPrimitive: CONTENT_TYPES.ROW_ITEM,
      },
      {
        configPrimitive: CONTENT_TYPES.MAIN_ROW,
      }
    ],
    viewClass: "image-item",
    contentContent: {},
    styleAltText: "",
    stylePadding: "Not supported on images - nest the item in a table"
  };
  
  var RICH_TEXT = {
    ...CELL_ITEM,
    configId: "RICH_TEXT",
    configName: "Text",
    configDescription: "A rich text component for arbitrary content.",
    contentContent: "<p>Click here to enter text.</p>",
    configPrimitive: CONTENT_TYPES.RICH_TEXT,
    configCategory: CONTENT_DEFINITION_CATEGORIES.STANDARD_COMPONENTS,
    viewAttributes: {
      table: {
        borderSpacing: 0,
      },
      td: {
        valign: "top",
      },
    },
    viewClass: "rich-text",
    configEditorType: "full",
    configEditorRoot: "p",
  };
  
  var TOC_ITEM = {
    ...CELL_ITEM,
    configId: "TOC_ITEM",
    configName: "ToC",
    configDescription: "A dynamic table of contents element",
    configCategory: CONTENT_DEFINITION_CATEGORIES.DYNAMIC_COMPONENT,
    configPrimitive: CONTENT_TYPES.TOC_ITEM,
    viewClass: "toc-item",
    viewCss: {
      "p": {
        "margin": "0 0 5px 0",
        "font-size" : "12px",
      }
    }
  };
  
  /**** Abstracted Elements ****/
  
    // The root item controls most of the default styles.
  var ROOT_ITEM = {
    ...TABLE_ITEM,
    toolboxHide: true,
    configId: "ROOT_ITEM",
    configName: "Template root element",
    configDescription: "This is the root element of the template. There should only be one, so don't try to copy it!",
    editorAllowedParents: [],
    contentIsRoot: true,
    editorSelectable: true,
    editorDraggable: false,
    editorDragTarget: true,
    editorHideable: false,
    editorCloneable: false,
    editorDeletable: false,
    editorSaveable: false,
    templateSettings: {
      backgroundColor: "#eeeeee",
    },
    viewClass: TABLE_ITEM.viewCss + " root-table",
    viewCss: {
      " ": {
        "Margin": "0",
        "border-spacing": "0",
        "font-family": "arial, sans-serif",
        "font-size": "14px",
        "line-height": "18px",
        "color": "#231f20",
        "background-color": "#eeeeee",
        "width": "100%",
        "height": "100%",
        "border-width": 0,
      },
      // Basic elements
      "table, tr, td": {
        "font-family": "arial, sans-serif",
        "font-size": "14px",
        "line-height": "18px",
        "color": "#231f20",
      },
      "table": {
        "border-spacing": 0,
        "border-collapse": "collapse",
        "width": "100%",
      },
      "td": {
        "padding-top": 0,
        "padding-right": 0,
        "padding-bottom": 0,
        "padding-left": 0,
      },
      "tr": {},
      "a[href]" : {
        "color": "#7f58a5",
        "text-decoration": "underline",
      },
      // Reset margins
      "h1, h2, h3, h4, h5, h6, p, ul, ol, li, hr": {
        "Margin-top": 0,
        "Margin-right": 0,
        "Margin-bottom": "5px",
        "Margin-left": 0,
      },
      // Headers
      "h1, h2, h3, h4, h5, h6": {
        "font-weight": "normal",
        "font-size": "18px",
        "line-height": "24px",
        //"color": "#7f58a5",
      },
      "h1": {
        "font-size": "26px",
        "line-height": "36px",
        //"color": "#ffffff"
      },
      "h2": {
        "font-size": "24px",
        //"line-height": "34px",
      },
      // Regular content
      "p, ul, ol": {
        "Margin-bottom": "20px",
      },
      "p:last-of-type": {
        "Margin-bottom": 0,
      },
      "hr": {
        "color": "#000000",
      },
      // Hacks
      "img": {
        "display": "block",
        "margin-top": 0,
        "margin-right": 0,
        "margin-bottom": 0,
        "margin-left": 0,
        "padding-top": 0,
        "padding-right": 0,
        "padding-bottom": 0,
        "padding-left": 0,
      },
      "ul": { "list-style-type": "disc",},
      "ul > li > ul": { "list-style-type": "circle",},
      "ul > li > ul > li > ul": { "list-style-type": "square",},
      
      "ol": { "list-style-type": "disc",},
      "ol > li > ol": { "list-style-type": "circle",},
      "ol > li > ol > li > ol": { "list-style-type": "square",},
      
      "li": { "margin-left": "25px",},
      "li > ul, li > ol": { "margin-bottom": "5px",},
      
      "p a": {
        "Margin": "inherit",
        "Margin-left": 0,
        "Margin-right": 0,
      },
      
      "strong, b": { "font-weight": "bold" },
      "em, i": { "font-style": "italic" },
      "u": { "text-decoration": "underline" },
      "s": { "text-decoration": "line-through" },
  
      "sup, sub": { "font-size": "0.8em" },
      "sup": { "vertical-align": "super" },
      "sub": { "vertical-align": "sub" },
      
      // Editor elements
      ".full-width": {
        "width": "100%",
      },
      ".small": {
        "font-size": "10px",
        "line-height": "11px",
      },
      ".editable": {
        "text-align": "left",
        "padding-top": "10px",
        "padding-right": "20px",
        "padding-bottom": "10px",
        "padding-left": "20px",
      },
      ".editable *": {
        "padding-top": 0,
        "padding-right": 0,
        "padding-bottom": 0,
        "padding-left": 0,
      },
      ".section-content-container": {
        "width": "620px",
        "background-color": "#ffffff"
      }
    },
  };
  
  var SECTION = {
    ...BASE_DEFINITION,
    configId: "SECTION",
    configName: "Section",
    configDescription: "A basic content section.",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STRUCTURAL_COMPONENTS,
    configPrimitive: CONTENT_TYPES.MAIN_ROW,
    editorAllowedParents: [
      {
        configId: "ROOT_ITEM",
      }
    ],
    viewClass: "main-row",
  };
  
  var COLUMN = {
    ...BASE_DEFINITION,
    configId: "COLUMN",
    configName: "Column",
    configDescription: "A single content column - only necessary for multiple nested rows.",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STRUCTURAL_COMPONENTS,
    configPrimitive: CONTENT_TYPES.COLUMN,
    editorAllowedParents: [
      {
        configId: "SECTION",
      }
    ],
    viewClass: "content-column",
  };
  
  var MAIN_ROW = {
    ...ROW_ITEM,
    toolboxHide: true,
    configId: "MAIN_ROW",
    configName: "Template section",
    configDescription: "A main section for the template. Automatically generates several subcomponents.",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STANDARD_COMPONENTS,
    editorAllowedParents: [
      {
        configId: "ROOT_ITEM",
      }
    ],
    viewClass: "main-row",
    editorDragTarget: false,
    configChildren: [
      {
        configId: "CELL_ITEM",
        editorLocked: true,
        configChildren: [
          {
            configId: "TABLE_ITEM",
            viewClass: "full-width",
            editorLocked: true,
            configChildren: [
              {
                configId: "ROW_ITEM",
                editorLocked: true,
                configChildren: [
                  {
                    configId: "CELL_ITEM",
                      editorLocked: true,
                    configChildren: [
                      {
                        configId: "TABLE_ITEM",
                        styleWidth: "620px",
                        styleBackgroundColour: "#ffffff",
                        editorLocked: true,
                        configChildren: [
                          {
                            configId: "ROW_ITEM",
                            editorSelectable: false,
                            editorDraggable: false,
                            editorHideable: false,
                            editorCloneable: false,
                            editorDeletable: false,
                            editorEditable: false,
                            editorSaveable: false,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
  
  var SECTION_HEADER = {
    ...RICH_TEXT,
    configId: "SECTION_HEADER",
    configName: "Header",
    configDescription: "A simple header to place at the top of each section",
    configEditorType: "basic",
    configEditorRoot: "h2",
    contentContent: "<h2>Header Text</h2>",
    styleTextColour: "#6e9bb0",
    viewClass: RICH_TEXT.viewClass + " section-header"
  };
  
  var AUTHOR_BLOCK = {
    ...CELL_ITEM,
    configId: "AUTHOR_BLOCK",
    configName: "Author",
    configDescription: "A block to display the author of this email",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STANDARD_COMPONENTS,
    stylePadding: "20px",
    viewCss: {
      "p": {
        "font-size": "12px",
      }
    },
    viewClass: CELL_ITEM.viewClass + " author-block",
    configChildren: [
      {
        configId: "TABLE_ITEM",
        editorLocked: true,
        configChildren: [
          {
            configId: "ROW_ITEM",
            editorLocked: true,
            configChildren: [
              {
                configId: "IMAGE_ITEM",
                editorLocked: false,
                editorDraggable: false,
                styleTextAlignment: "left",
                styleWidth: "120px",
                configChildren: [],
              }
            ],
          },
          {
            configId: "ROW_ITEM",
            editorLocked: true,
            configChildren: [
              {
                configId: "RICH_TEXT",
                editorLocked: false,
                editorDraggable: false,
                contentContent: "<p><strong>Author</strong><br/>Job Title</p>",
                stylePadding: "5px 0",
                configChildren: [],
              }
            ],
          }
        ],
      }
    ]
  };
  
  var TEAM_MEMBER_ITEM = {
    ...CELL_ITEM,
    configId: "TEAM_MEMBER_ITEM",
    configName: "Team Member",
    configDescription: "A block to display a team member",
    configCategory: CONTENT_DEFINITION_CATEGORIES.STANDARD_COMPONENTS,
    stylePadding: "20px",
    viewCss: {
      "p": {
        "font-size": "12px",
      }
    },
    viewClass: CELL_ITEM.viewClass + " team-member-item",
    configChildren: [
      {
        configId: "TABLE_ITEM",
        editorLocked: true,
        styleWidth: "160px",
        configChildren: [
          {
            configId: "ROW_ITEM",
            editorLocked: true,
            configChildren: [
              {
                configId: "IMAGE_ITEM",
                editorLocked: false,
                editorDraggable: false,
                styleWidth: "120px",
                configChildren: [],
              }
            ],
          },
          {
            configId: "ROW_ITEM",
            editorLocked: true,
            configChildren: [
              {
                configId: "RICH_TEXT",
                editorLocked: false,
                editorDraggable: false,
                styleTextAlignment: "center",
                contentContent: "<p><strong>Author</strong><br/>Job Title<br/><a href='mailto:email@abi.org.uk'>email@abi.org.uk</a></p>",
                stylePadding: "5px 0",
                configChildren: [],
              }
            ],
          }
        ],
      }
    ]
  };
  
  return [
    TABLE_ITEM,
    ROW_ITEM,
    CELL_ITEM,
    IMAGE_ITEM,
    ROOT_ITEM,
    MAIN_ROW,
    RICH_TEXT,
    SECTION_HEADER,
    TOC_ITEM,
    AUTHOR_BLOCK,
    TEAM_MEMBER_ITEM,
    SECTION,
    COLUMN,
  ]
})();

export {
  CONTENT_DEFINITIONS,
};


// WEBPACK FOOTER //
// ./src/App/Editor/state/config/initialConfig/itemDefinitions.js